import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUp, RefreshCw } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNewOwnerPosts, useOwnerFeed } from '@/hooks/useOwnerFeed';
import { ownerPubkey } from '@/lib/owner';
import { FollowButton } from '@/components/FollowButton';
import { NoteCard } from '@/components/NoteCard';
import { SectionHeading } from '@/components/SectionHeading';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

function NoteSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-3/5" />
        <div className="flex items-center justify-between pt-2">
          <Skeleton className="h-3 w-10" />
          <div className="flex gap-2">
            <Skeleton className="h-7 w-10 rounded-full" />
            <Skeleton className="h-7 w-10 rounded-full" />
            <Skeleton className="h-7 w-10 rounded-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function NotesFeed() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const feed = useOwnerFeed();
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = feed;
  const [since, setSince] = useState<number | null>(null);

  const posts = useMemo(() => {
    const seen = new Set<string>();
    return (feed.data?.pages.flat() ?? [])
      .filter((event) => {
        if (seen.has(event.id)) return false;
        seen.add(event.id);
        return true;
      })
      .sort((a, b) => b.created_at - a.created_at);
  }, [feed.data]);

  // Baseline timestamp for new-post detection (the newest post we already show).
  useEffect(() => {
    if (since === null && posts[0]) setSince(posts[0].created_at);
  }, [since, posts]);

  const { data: newPosts } = useNewOwnerPosts(since);

  const { ref, inView } = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loadNewPosts = async () => {
    setSince(null);
    await queryClient.resetQueries({ queryKey: ['owner-feed'] });
  };

  const newCount = newPosts?.length ?? 0;

  return (
    <section id="notes" className="container scroll-mt-24 py-20 md:py-28">
      <div className="mx-auto max-w-2xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading
            kicker="// notes"
            title="What I've been up to"
            sub={
              user
                ? 'Live from the Nostr relays — like, reply, and boost away.'
                : 'Live from the Nostr relays. Log in with Nostr to like, reply, and boost.'
            }
          />
          {ownerPubkey && <FollowButton pubkey={ownerPubkey} className="shrink-0" />}
        </div>

        <div className="relative mt-10 space-y-4">
          {newCount > 0 && (
            <div className="sticky top-20 z-10 -mt-2 flex justify-center">
              <button
                onClick={loadNewPosts}
                className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/25 transition-transform hover:scale-105 active:scale-100 motion-reduce:transition-none"
              >
                <ArrowUp className="size-3.5" />
                {newCount} new {newCount === 1 ? 'post' : 'posts'}
              </button>
            </div>
          )}

          {feed.isPending ? (
            <>
              <NoteSkeleton />
              <NoteSkeleton />
              <NoteSkeleton />
            </>
          ) : feed.isError ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-4 px-8 py-12 text-center">
                <p className="max-w-sm text-muted-foreground">
                  Couldn't reach the Nostr relays. Check your connection and try again.
                </p>
                <Button variant="outline" onClick={() => feed.refetch()}>
                  <RefreshCw className="size-4" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : posts.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="px-8 py-12 text-center">
                <p className="mx-auto max-w-sm text-muted-foreground">
                  No notes found. If this is your site, double-check the npub in{' '}
                  <code className="font-mono text-primary">src/config/site.ts</code>.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {posts.map((event) => (
                <NoteCard key={event.id} event={event} />
              ))}

              <div ref={ref} className="py-2">
                {isFetchingNextPage && <NoteSkeleton />}
                {!hasNextPage && posts.length > 5 && (
                  <p className="py-4 text-center font-mono text-xs text-muted-foreground">
                    ~ you're all caught up ~
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
